import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemHealthIndicators = () => {
  const [systemStatus, setSystemStatus] = useState({});
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const systemComponents = [
    {
      id: 'database',
      name: 'Datenbank',
      status: 'healthy',
      responseTime: 45,
      uptime: 99.9,
      lastCheck: new Date(Date.now() - 30000),
      details: 'PostgreSQL Cluster aktiv'
    },
    {
      id: 'auth',
      name: 'Authentifizierung',
      status: 'healthy',
      responseTime: 120,
      uptime: 99.8,
      lastCheck: new Date(Date.now() - 45000),
      details: 'Google OAuth funktional'
    },
    {
      id: 'notifications',
      name: 'Benachrichtigungen',
      status: 'warning',
      responseTime: 890,
      uptime: 98.5,
      lastCheck: new Date(Date.now() - 60000),
      details: 'Email-Service verzögert'
    },
    {
      id: 'api',
      name: 'API Gateway',
      status: 'healthy',
      responseTime: 78,
      uptime: 99.7,
      uptime: 99.7,
      lastCheck: new Date(Date.now() - 20000),
      details: 'Alle Endpunkte verfügbar'
    },
    {
      id: 'calendar',
      name: 'Kalender-Sync',
      status: 'error',
      responseTime: 0,
      uptime: 95.2,
      lastCheck: new Date(Date.now() - 300000),
      details: 'Google Calendar API Fehler'
    },
    {
      id: 'backup',
      name: 'Backup-System',
      status: 'healthy',
      responseTime: 234,
      uptime: 100,
      lastCheck: new Date(Date.now() - 3600000),
      details: 'Letztes Backup: 03:00 Uhr'
    }
  ];

  const performanceMetrics = {
    activeUsers: 127,
    totalSessions: 1543,
    avgResponseTime: 156,
    errorRate: 0.2,
    throughput: 2340,
    memoryUsage: 68,
    cpuUsage: 42,
    diskUsage: 34
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-success bg-success/10 border-success/20';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'error':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'healthy':
        return 'Gesund';
      case 'warning':
        return 'Warnung';
      case 'error':
        return 'Fehler';
      default:
        return 'Unbekannt';
    }
  };

  const formatResponseTime = (time) => {
    if (time === 0) return 'N/A';
    return time < 1000 ? `${time}ms` : `${(time / 1000)?.toFixed(1)}s`;
  };

  const formatLastCheck = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min`;
    return timestamp?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const overallHealth = systemComponents?.filter(comp => comp?.status === 'healthy')?.length / systemComponents?.length * 100;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Activity" size={18} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">System-Status</h3>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-md border text-sm font-medium ${
            overallHealth >= 90 ? 'text-success bg-success/10 border-success/20' :
            overallHealth >= 70 ? 'text-warning bg-warning/10 border-warning/20': 'text-error bg-error/10 border-error/20'
          }`}>
            {overallHealth?.toFixed(0)}% Gesund
          </div>
          <Button variant="ghost" size="icon">
            <Icon name="RefreshCw" size={16} />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {systemComponents?.map((component) => (
          <div key={component?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name={getStatusIcon(component?.status)} size={16} className={
                  component?.status === 'healthy' ? 'text-success' :
                  component?.status === 'warning' ? 'text-warning' : 'text-error'
                } />
                <h4 className="font-medium text-foreground">{component?.name}</h4>
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(component?.status)}`}>
                {getStatusLabel(component?.status)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-2 text-sm">
              <div>
                <span className="text-muted-foreground">Antwortzeit: </span>
                <span className="font-medium text-foreground">
                  {formatResponseTime(component?.responseTime)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Verfügbarkeit: </span>
                <span className="font-medium text-foreground">{component?.uptime}%</span>
              </div>
            </div>

            <div className="text-xs text-muted-foreground mb-2">
              {component?.details}
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                Letzte Prüfung: {formatLastCheck(component?.lastCheck)}
              </span>
              <div className={`w-2 h-2 rounded-full ${
                component?.status === 'healthy' ? 'bg-success' :
                component?.status === 'warning' ? 'bg-warning' : 'bg-error'
              }`} />
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-border pt-4">
        <h4 className="font-medium text-foreground mb-4">Performance-Metriken</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-primary">{performanceMetrics?.activeUsers}</div>
            <div className="text-xs text-muted-foreground">Aktive Benutzer</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{performanceMetrics?.avgResponseTime}ms</div>
            <div className="text-xs text-muted-foreground">Ø Antwortzeit</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-success">{performanceMetrics?.errorRate}%</div>
            <div className="text-xs text-muted-foreground">Fehlerrate</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-accent">{performanceMetrics?.throughput}</div>
            <div className="text-xs text-muted-foreground">Anfragen/Min</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">CPU</span>
              <span className="text-xs font-medium">{performanceMetrics?.cpuUsage}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${performanceMetrics?.cpuUsage}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Speicher</span>
              <span className="text-xs font-medium">{performanceMetrics?.memoryUsage}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-warning h-2 rounded-full transition-all duration-300"
                style={{ width: `${performanceMetrics?.memoryUsage}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Festplatte</span>
              <span className="text-xs font-medium">{performanceMetrics?.diskUsage}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-success h-2 rounded-full transition-all duration-300"
                style={{ width: `${performanceMetrics?.diskUsage}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
          <span>Letzte Aktualisierung: {lastUpdate?.toLocaleTimeString('de-DE')}</span>
          <span>Nächste Prüfung in 30s</span>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthIndicators;